// FR-07's sync control (P3.9), and the readout that makes AC-2 checkable by looking.
//
// ## Why this is a button and not a toggle
//
// **Sync is not a mode**, which P3.9 states outright: "dropping it leaves the deck where it is,
// playing, at whatever rate it reached". There is no synced state to be in and therefore nothing to
// un-press. One action does two things — matches tempo through the pitch fader, then asks the audio
// thread to align the beat — and afterwards the deck is simply a deck playing at a rate someone
// chose. A toggle would be claiming a mode the engine does not have, and the first thing a user
// would do with it is press it again and wonder what changed.
//
// ## The readout, and why it is here at all
//
// AC-2 asks for "no audible phase flam (alignment within ±10 ms)". Ten milliseconds is under half a
// render quantum — P3.9 says it "cannot be verified by eye and must be measured" — so the number is
// measured **on the audio thread**, published into the deck's status buffer every block, and read
// here. That makes the claim falsifiable by looking, and it shows the failure mode FR-07 is actually
// judged on: two decks whose tempos do not quite match drift apart, and a static "synced" badge
// would hide exactly that.
//
// Written straight to the DOM from a rAF loop, never through React state — Stack §2, the same rule
// the meters and the elapsed clock follow. This changes 375 times a second at the source.

import { useEffect, useRef, type JSX } from 'react';

import type { SyncPartner, TrackLoaderApi } from './useTrackLoader';

/** AC-2's budget, in milliseconds. The readout says which side of it the decks are on. */
export const FLAM_BUDGET_MS = 10;

/**
 * Phase error in seconds → what the readout says.
 *
 * `NaN` is "there is nothing to compare against" and gets an em dash rather than a number, because
 * zero would be a claim of perfect alignment between decks that have never heard of each other —
 * see `DECK_STATUS.SYNC_PHASE_ERROR_SECONDS`.
 */
export function formatPhaseError(seconds: number): string {
  if (!Number.isFinite(seconds)) {
    return '—';
  }
  const ms = seconds * 1000;
  return `${ms.toFixed(1)} ms ${ms <= FLAM_BUDGET_MS ? 'in sync' : 'out'}`;
}

export interface SyncPanelProps {
  readonly loader: TrackLoaderApi;
  /** The other deck. Optional so a single-deck surface stays expressible; the app always passes it. */
  readonly partner?: SyncPartner | undefined;
}

export function SyncPanel({ loader, partner }: SyncPanelProps): JSX.Element {
  const errorRef = useRef<HTMLSpanElement | null>(null);
  const { readSyncPhaseError } = loader;

  useEffect(() => {
    let frame = 0;
    const tick = (): void => {
      frame = requestAnimationFrame(tick);
      const node = errorRef.current;
      if (node !== null) {
        node.textContent = formatPhaseError(readSyncPhaseError());
      }
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [readSyncPhaseError]);

  const ready = loader.beatGrid !== null && partner?.beatGrid != null;
  const deck = loader.deckId.toUpperCase();

  return (
    <div className="stack stack--tight">
      {/* Disabled with a reason rather than hidden: "why is sync greyed out" is answerable and
          "where did sync go" is not. */}
      <button
        type="button"
        className="transport__sync"
        disabled={!ready}
        onClick={() => {
          if (partner !== undefined) {
            loader.sync(partner);
          }
        }}
        title={
          loader.beatGrid === null
            ? 'No beatgrid on this deck — sync needs a detected tempo.'
            : partner?.beatGrid == null
              ? 'The other deck has no beatgrid to sync to.'
              : 'Match the other deck’s tempo and align the beat.'
        }
        aria-label={`Sync deck ${deck} to the other deck`}
      >
        SYNC
      </button>

      <div className="row transport__times">
        <span className="micro">Phase</span>
        <span ref={errorRef} className="mono transport__phase-error">
          —
        </span>
      </div>

      {loader.syncNote !== null && (
        <span className="micro notice notice--warning" role="status">
          Sync: {loader.syncNote}
        </span>
      )}
    </div>
  );
}
